import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { Album, AlbumCatalogue, AlbumFiguresMap, AlbumMap, AlbumPage, AlbumPicture, AlbumSpread, AlbumStyles, AlbumTemplate, AlbumView, ItineraryDay, JournalEntry, TripPhoto, TripSummary } from '../../types';
import { albumApi, type AlbumPageLook } from '../../api/album.api';
import { usePhotoUploads } from '../../hooks/usePhotoUploads';
import UploadProgressBar from '../ui/UploadProgressBar';
import { toast } from '../ui/Toast';
import ConfirmModal from '../ui/ConfirmModal';
import { BookOpen, Icon, RefreshCw } from '../ui/Icon';
import AlbumSpreadView from './AlbumSpreadView';
import AlbumBook from './AlbumBook';
import AlbumSpreadList from './AlbumSpreadList';
import AlbumSpineTools from './AlbumSpineTools';
import { pageName, pagesOf, placeOf, spreadOfPage, spreadWidth } from '../../utils/albumSpreads';
import AlbumPageSettings from './AlbumPageSettings';
import AlbumPhotoPicker from './AlbumPhotoPicker';
import AlbumTextTools from './AlbumTextTools';
import { albumFonts, maxCharsAt, slotPt } from '../../utils/albumLook';
import AlbumDayPicker from './AlbumDayPicker';
import AlbumStylePanel from './AlbumStylePanel';

interface Props {
  tripId: string;
  album: Album;
  catalogue: AlbumCatalogue;
  summary: TripSummary | null;
  maps: Record<string, AlbumMap>;
  trip: AlbumView['trip'];
  styles: AlbumStyles;
  photos: TripPhoto[];
  entries: JournalEntry[];
  days: ItineraryDay[];
  onChange: (album: Album) => void;
  /** Opens the wizard again (ADR-0050). */
  onRestart: () => void;
  /** ADR-0049: the whole view changes with the format — the parent re-reads it. */
  onFormat: (format: string) => Promise<void>;
}

/** A spread added from the editor starts with one photo a page; the layout is a click away. */
const NEW_SPREAD_TEMPLATE: AlbumTemplate = 'ONE_PHOTO';

/** The page a spread opens on when picked: the front of the cover, the left of a spread. */
function firstPage(spread: AlbumSpread | undefined): AlbumPage | undefined {
  if (!spread) return undefined;
  if (spread.kind === 'COVER') return spread.pages.find(p => p.side !== 'LEFT') ?? spread.pages[0];
  return spread.pages[0];
}

const hasContent = (page: AlbumPage) => page.slots.some(s => s.photoId || s.albumPhotoId || s.text || s.dayId);

/** ADR-0046/0052: the traveller's own album — spreads on the left, the open book in the middle, the page's tools on the right. */
export default function AlbumEditor({ tripId, album, catalogue, summary, maps, trip, styles, photos, entries, days, onChange, onRestart, onFormat }: Props) {
  const { t } = useTranslation();
  const [selectedPageId, setSelectedPageId] = useState<string | null>(
    () => firstPage(album.spreads.find(s => s.kind === 'INNER') ?? album.spreads[0])?.id ?? null,
  );
  const [selectedIndex, setSelectedIndex] = useState<number | null>(null);
  const [saving, setSaving] = useState(false);
  const { progress, uploading, uploadAll } = usePhotoUploads();
  const [confirm, setConfirm] = useState<'spread' | 'redoDay' | 'join' | null>(null);
  const [pendingTemplate, setPendingTemplate] = useState<AlbumTemplate | null>(null);
  // What's selected on the page lives in «Page»; the album's own look in «Album».
  const [tab, setTab] = useState<'page' | 'album'>('page');
  const [leafing, setLeafing] = useState(false);

  // ADR-0051: the album's own photos paint like the trip's; only the write tells them apart.
  const ownIds = useMemo(() => new Set(album.photos.map(p => p.id)), [album.photos]);
  const pictures = useMemo<AlbumPicture[]>(() => [...album.photos, ...photos], [album.photos, photos]);
  const photosById = useMemo(() => new Map(pictures.map(p => [p.id, p])), [pictures]);
  const usedIds = useMemo(
    () => new Set(pagesOf(album).flatMap(p => p.slots.map(s => s.photoId ?? s.albumPhotoId).filter((id): id is string => !!id))),
    [album],
  );
  // A page that's gone (its spread deleted, a panorama split, the album started over) falls back to the first one.
  const spread = spreadOfPage(album, selectedPageId) ?? album.spreads.find(s => s.kind === 'INNER') ?? album.spreads[0];
  const page = spread?.pages.find(p => p.id === selectedPageId) ?? firstPage(spread) ?? null;
  const spreadIndex = spread ? album.spreads.indexOf(spread) : -1;
  const frames = page ? catalogue.templates.find(tp => tp.id === page.template)?.slots ?? [] : [];
  const frame = selectedIndex !== null ? frames[selectedIndex] : undefined;
  const slot = page && selectedIndex !== null ? page.slots.find(s => s.index === selectedIndex) : undefined;

  async function run(write: () => Promise<Album>): Promise<Album | null> {
    setSaving(true);
    try {
      const next = await write();
      onChange(next);
      return next;
    } catch {
      toast(t('album.saveError'), 'error');
      return null;
    } finally {
      setSaving(false);
    }
  }

  function selectPage(pageId: string | null) {
    setSelectedPageId(pageId);
    setSelectedIndex(null);
  }

  const inner = album.spreads.filter(s => s.kind === 'INNER');

  function selectSpread(spreadId: string) {
    selectPage(firstPage(album.spreads.find(s => s.id === spreadId))?.id ?? null);
  }

  /** ADR-0052: a new spread goes after the one open — at the start of the inner ones after the cover or the opening. */
  async function addSpread() {
    const at = spread?.kind === 'INNER' ? inner.indexOf(spread) + 1 : spread?.kind === 'CLOSING' ? inner.length : 0;
    const next = await run(() => albumApi.addSpread(tripId, NEW_SPREAD_TEMPLATE, at));
    if (next) selectPage(firstPage(next.spreads.filter(s => s.kind === 'INNER')[at])?.id ?? null);
  }

  async function reorder(spreadIds: string[]) {
    const byId = new Map(album.spreads.map(s => [s.id, s]));
    const fixed = (kind: AlbumSpread['kind']) => album.spreads.filter(s => s.kind === kind);
    onChange({ ...album, spreads: [...fixed('COVER'), ...fixed('OPENING'), ...spreadIds.map(id => byId.get(id)!), ...fixed('CLOSING')] });
    await run(() => albumApi.reorderSpreads(tripId, spreadIds));
  }

  async function deleteSpread() {
    if (!spread || spread.kind !== 'INNER') return;
    const next = await run(() => albumApi.removeSpread(tripId, spread.id));
    setConfirm(null);
    if (next) selectPage(firstPage(next.spreads[Math.min(spreadIndex, next.spreads.length - 1)])?.id ?? null);
  }

  function swapSides() {
    if (spread) void run(() => albumApi.swapSides(tripId, spread.id));
  }

  /** ADR-0052: a spread layout on one page joins it with the facing one — asked first if that one has something. */
  async function setTemplate(template: AlbumTemplate, confirmed = false) {
    if (!page || !spread) return;
    const joining = placeOf(catalogue, template) !== 'page' && page.side !== 'BOTH';
    if (joining && !confirmed && spread.pages.some(p => p.id !== page.id && hasContent(p))) {
      setPendingTemplate(template);
      setConfirm('join');
      return;
    }
    await run(() => albumApi.changeTemplate(tripId, page.id, template));
    setConfirm(null);
    setSelectedIndex(null);
  }

  function setTheme(theme: string) {
    void run(() => albumApi.setTheme(tripId, theme));
  }

  function setLook({ backgroundPhotoId, ...look }: AlbumPageLook) {
    if (!page) return;
    const photo = backgroundPhotoId && ownIds.has(backgroundPhotoId)
      ? { backgroundAlbumPhotoId: backgroundPhotoId }
      : backgroundPhotoId !== undefined ? { backgroundPhotoId } : {};
    void run(() => albumApi.setPageLook(tripId, page.id, { ...look, ...photo }));
  }

  function setFiguresMap(figuresMap: AlbumFiguresMap) {
    if (!page) return;
    void run(() => albumApi.setFiguresMap(tripId, page.id, figuresMap));
  }

  function setFigures(figures: { figuresRoute?: boolean; figuresOutline?: boolean; mapLegend?: boolean }) {
    if (!page) return;
    void run(() => albumApi.setFigures(tripId, page.id, figures));
  }

  function setPhoto(index: number, photoId: string | null) {
    if (!page) return;
    const content = photoId && ownIds.has(photoId) ? { albumPhotoId: photoId } : { photoId };
    void run(() => albumApi.setSlot(tripId, page.id, index, content));
  }

  async function uploadOwn(files: File[]) {
    // Each one shows up as soon as it's stored, not at the end of the batch.
    await uploadAll(files, async (photo, onProgress) => {
      const next = await albumApi.uploadPhoto(tripId, photo.file, onProgress);
      onChange(next);
      return next;
    });
  }

  function removeOwn(photoId: string) {
    void run(() => albumApi.removePhoto(tripId, photoId));
  }

  async function redoDay() {
    if (!spread?.dayId) return;
    const position = spreadIndex;
    const next = await run(() => albumApi.redoDay(tripId, spread.dayId!));
    setConfirm(null);
    if (next) selectPage(firstPage(next.spreads[Math.min(position, next.spreads.length - 1)])?.id ?? null);
  }

  function setDay(index: number, dayId: string | null) {
    if (!page) return;
    void run(() => albumApi.setSlot(tripId, page.id, index, { dayId }));
  }

  function setText(text: string | null) {
    if (!page || selectedIndex === null) return;
    void run(() => albumApi.setSlot(tripId, page.id, selectedIndex, { text }));
  }

  const ownPhotos = { photos: album.photos, uploading, onUpload: uploadOwn, onRemove: removeOwn };
  const daySpreadCount = spread?.dayId ? album.spreads.filter(s => s.dayId === spread.dayId).length : 0;
  const name = page ? pageName(album.spreads, page) : null;
  const label = !name ? ''
    : 'cover' in name ? t(`album.spread.${name.cover}`)
      : name.from === name.to ? t('album.page', { number: name.from }) : t('album.spread.pages', { pages: `${name.from}–${name.to}` });
  const viewProps = { catalogue, photosById, summary, maps, trip, styles, albumStyle: album };
  const handlersFor = (p: AlbumPage) => ({
    onSlotClick: (index: number) => { setSelectedIndex(index); setTab('page'); },
    onPhotoDrop: (index: number, photoId: string) => {
      setSelectedIndex(index);
      const content = ownIds.has(photoId) ? { albumPhotoId: photoId } : { photoId };
      void run(() => albumApi.setSlot(tripId, p.id, index, content));
    },
    onPhotoRemove: (index: number) => void run(() => albumApi.setSlot(tripId, p.id, index, { photoId: null })),
    onPhotoMove: (index: number, { x, y }: { x: number; y: number }) => void run(() => albumApi.setSlot(tripId, p.id, index, { photoX: x, photoY: y })),
    onTextCommit: (index: number, text: string | null) => void run(() => albumApi.setSlot(tripId, p.id, index, { text })),
  });

  return (
    // ADR-0052: a spread is twice as wide as tall — it gets the whole width, with
    // the spreads in a strip under it, as in the photo-book editors. The panel
    // scrolls on its own, never the whole view.
    <div className="grid gap-4 md:h-full md:min-h-0 md:grid-cols-[minmax(0,1fr)_21rem] md:grid-rows-[minmax(0,1fr)_auto]">
      {/* The selection rings sit outside each box: the padding keeps them from being clipped. */}
      <div className="md:col-start-1 md:row-start-2 min-w-0 p-1.5">
        <AlbumSpreadList
          {...viewProps}
          spreads={album.spreads}
          selectedId={spread?.id ?? null}
          onSelect={selectSpread}
          onReorder={reorder}
          onAdd={addSpread}
          onDelete={id => { selectSpread(id); setConfirm('spread'); }}
        />
      </div>

      {/* Sized by its row only on wide screens; on a phone the spread just takes the width. */}
      <div className="md:col-start-1 md:row-start-1 md:min-h-0 flex justify-center md:items-center md:[container-type:size]">
        {spread && (
          <div className="w-full" style={{ maxWidth: `min(100%, calc(100cqh * ${spreadWidth(catalogue, spread.kind)} / ${catalogue.page.height}))` }}>
            <AlbumSpreadView
              {...viewProps}
              spread={spread}
              selectedPageId={page?.id ?? null}
              selectedIndex={selectedIndex}
              onSelectPage={id => { if (id !== page?.id) selectPage(id); }}
              pageHandlers={handlersFor}
            />
          </div>
        )}
      </div>

      <aside className="md:col-start-2 md:row-start-1 md:row-span-2 md:min-h-0 md:overflow-y-auto md:overflow-x-hidden md:px-1 md:pb-1">
        <div className="sticky top-0 z-10 pb-3 bg-slate-50 dark:bg-slate-950 flex items-center gap-2">
          <div className="flex-1 flex bg-slate-100 dark:bg-slate-800 rounded-xl p-0.5" role="tablist">
            {(['page', 'album'] as const).map(id => (
              <button
                key={id}
                role="tab"
                aria-selected={tab === id}
                onClick={() => setTab(id)}
                className={`flex-1 px-3 py-1.5 rounded-lg text-xs font-semibold cursor-pointer ${
                  tab === id ? 'bg-white shadow-sm text-slate-900 dark:bg-slate-700 dark:text-slate-100' : 'text-slate-500'
                }`}
              >
                {t(`album.tabs.${id}`)}
              </button>
            ))}
          </div>
          <button type="button" onClick={() => setLeafing(true)} className="btn-secondary text-xs gap-1.5 shrink-0">
            <Icon as={BookOpen} size={14} />
            {t('album.preview.open')}
          </button>
        </div>

        {tab === 'page' ? (
          <div className="space-y-6">
            <section className="space-y-2">
              {frame?.kind === 'PHOTO' && page && selectedIndex !== null ? (
                <>
                  <div className="flex items-center justify-between">
                    <h3 className="text-xs font-semibold uppercase tracking-wider text-slate-500">{t('album.choosePhoto')}</h3>
                    {(slot?.photoId || slot?.albumPhotoId) && (
                      <button onClick={() => setPhoto(selectedIndex, null)} disabled={saving} className="text-xs text-slate-500 hover:text-red-500">
                        {t('album.removePhoto')}
                      </button>
                    )}
                  </div>
                  <AlbumPhotoPicker photos={photos} own={ownPhotos} usedIds={usedIds} selectedId={slot?.photoId ?? slot?.albumPhotoId ?? null} onPick={id => setPhoto(selectedIndex, id)} />
                </>
              ) : frame?.kind === 'MAP' && page && selectedIndex !== null ? (
                <>
                  <h3 className="text-xs font-semibold uppercase tracking-wider text-slate-500">{t('album.slot.MAP')}</h3>
                  {page.template === 'DAY_MAP' ? (
                    <AlbumDayPicker days={days} value={slot?.dayId ?? null} disabled={saving} onPick={dayId => setDay(selectedIndex, dayId)} />
                  ) : (
                    <p className="text-xs text-slate-500 dark:text-slate-400">{t('album.tripMapHint')}</p>
                  )}
                </>
              ) : frame && (frame.kind === 'TITLE' || frame.kind === 'TEXT') ? (
                <>
                  <AlbumTextTools
                    kind={frame.kind}
                    text={slot?.text ?? ''}
                    maxChars={maxCharsAt(catalogue.fontPt, frame, slotPt(styles, catalogue.fontPt, frame, slot))}
                    pt={slotPt(styles, catalogue.fontPt, frame, slot)}
                    range={styles.textPt[frame.kind]}
                    align={slot?.textAlign ?? frame.align ?? 'left'}
                    font={slot?.font ?? null}
                    styles={styles}
                    pairFont={frame.kind === 'TITLE' ? albumFonts(styles, album.fontPair).title : albumFonts(styles, album.fontPair).body}
                    onStyle={style => { if (page && selectedIndex !== null) void run(() => albumApi.setSlot(tripId, page.id, selectedIndex, style)); }}
                    entries={entries}
                    days={days}
                    saving={saving}
                    onSave={setText}
                  />
                </>
              ) : (
                <>
                  <p className="text-xs text-slate-500 dark:text-slate-400">{t('album.selectHint')}</p>
                  <AlbumPhotoPicker photos={photos} own={ownPhotos} usedIds={usedIds} selectedId={null} onPick={null} />
                </>
              )}
            </section>


            {page && spread && (
              <AlbumPageSettings
                page={page}
                spread={spread}
                label={label}
                catalogue={catalogue}
                saving={saving}
                onTemplate={template => void setTemplate(template)}
                onFiguresMap={setFiguresMap}
                onFigures={setFigures}
                onDeleteSpread={() => setConfirm('spread')}
                onSwap={swapSides}
                onRedoDay={() => setConfirm('redoDay')}
              />
            )}

            {spread?.kind === 'COVER' && (
              <AlbumSpineTools
                key={album.spineText ?? ''}
                spine={catalogue.spine}
                value={album.spineText}
                saving={saving}
                onSave={text => void run(() => albumApi.setSpineText(tripId, text))}
              />
            )}


            <AlbumStylePanel
              section="page"
          styles={styles}
          theme={album.theme}
          page={page}
          photos={pictures}
          saving={saving}
          formats={catalogue.formats}
          format={album.format}
          fontPair={album.fontPair}
          photoFrame={album.photoFrame}
          onAlbumStyle={style => void run(() => albumApi.setAlbumStyle(tripId, style))}
          onFormat={format => { setSaving(true); void onFormat(format).finally(() => setSaving(false)); }}
          onTheme={setTheme}
          onLook={setLook}
            />
          </div>
        ) : (
          <div className="space-y-6">
            <AlbumStylePanel
              section="album"
          styles={styles}
          theme={album.theme}
          page={page}
          photos={pictures}
          saving={saving}
          formats={catalogue.formats}
          format={album.format}
          fontPair={album.fontPair}
          photoFrame={album.photoFrame}
          onAlbumStyle={style => void run(() => albumApi.setAlbumStyle(tripId, style))}
          onFormat={format => { setSaving(true); void onFormat(format).finally(() => setSaving(false)); }}
          onTheme={setTheme}
          onLook={setLook}
            />
            <button onClick={onRestart} disabled={saving} className="btn-ghost-sm text-xs gap-1.5 text-slate-500">
              <Icon as={RefreshCw} size={12} />
              {t('album.restart')}
            </button>
          </div>
        )}
      </aside>

      {leafing && <AlbumBook {...viewProps} spreads={album.spreads} onClose={() => setLeafing(false)} />}
      <ConfirmModal
        open={confirm === 'spread'}
        title={t('album.spread.deleteConfirm')}
        onConfirm={deleteSpread}
        onCancel={() => setConfirm(null)}
        loading={saving}
      />
      <ConfirmModal
        open={confirm === 'join'}
        title={t('album.spread.joinTitle')}
        body={t('album.spread.joinBody')}
        confirmLabel={t('album.spread.join')}
        tone="neutral"
        onConfirm={() => { if (pendingTemplate) void setTemplate(pendingTemplate, true); }}
        onCancel={() => { setConfirm(null); setPendingTemplate(null); }}
        loading={saving}
      />
      <ConfirmModal
        open={confirm === 'redoDay'}
        title={t('album.redoDay.confirmTitle')}
        body={t('album.redoDay.confirmBody', { count: daySpreadCount * 2 })}
        onConfirm={redoDay}
        onCancel={() => setConfirm(null)}
        loading={saving}
      />
      <UploadProgressBar progress={progress} />
    </div>
  );
}
